import { Component } from 'react';
import Editor from 'draft-js-plugins-editor';
import { EditorState, ContentState, RichUtils, convertFromRaw } from 'draft-js';
import { isNil, omit } from 'lodash';

import styles from './StandardRichTextEditor.scss';
import './external.scss';
import { complionToolbarPlugin } from './ComplionToolbar/complionToolbarPlugins';
import {
    BoldButton,
    ItalicButton,
    UnderlineButton,
    UnorderedListButton,
    OrderedListButton,
    UndoButton,
    RedoButton
} from './ComplionToolbar/standardButtons';

const toolbarPlugin = complionToolbarPlugin({
    toolbarItems: [
        UndoButton,
        RedoButton,
        BoldButton,
        ItalicButton,
        UnderlineButton,
        UnorderedListButton,
        OrderedListButton
    ]
});
const { ToolbarHost } = toolbarPlugin;
import { boldStylePlugin } from './plugins/boldStylePlugin';
const plugins = [toolbarPlugin, boldStylePlugin()];

class StandardRichTextEditor extends Component {
    constructor(...args) {
        super(...args);
        this._offClickHandler = null;
        this._hasFocus = false;
        this.state = { editorState: StandardRichTextEditor.negotiateContent(this.props.content) };
        this.onChange = this._onChange.bind(this);
        this.handleKeyCommand = this._handleKeyCommand.bind(this);
        this.onTab = this._onTab.bind(this);
        this.onDelete = this._onDelete.bind(this);
        this.onBackspace = this._onBackspace.bind(this);
        this.onBlur = this._onBlur.bind(this);
    }

    static negotiateContent(content) {
        if (isNil(content)) {
            return EditorState.createWithContent(ContentState.createFromText(' '));
        } else if (typeof content === 'string') {
            try {
                let rawContentState = JSON.parse(content);
                return EditorState.createWithContent(rawContentState);
            } catch (e) {
                return EditorState.createWithContent(ContentState.createFromText(content));
            }
        } else {
            try {
                let contentState = convertFromRaw(content);
                return EditorState.createWithContent(contentState);
            } catch (e) {
                return EditorState.createWithContent(ContentState.createFromText(' '));
            }
        }
    }


    focus() {
        if (!this._hasFocus) {
            this._hasFocus = true;
            this.editor.focus();
        }
    }

    blur() {
        if (this._hasFocus) {
            this._hasFocus = false;
            this.editor.blur();
        }
    }

    componentDidMount() {
        const { autoFocus = true, onBlur } = this.props;
        if(autoFocus) {
            this.focus();
        }
        this._createOffClickHandler();
        if(typeof onBlur === 'function') {
            onBlur(this.state.editorState);
        }
    }

    componentWillUnmount() {
        this._removeOffClickHandler();
    }

    _createOffClickHandler() {
        if (typeof this._offClickHandler !== 'function') {
            this._offClickHandler = document.addEventListener('click', () => { this.blur(); }, true);
        }
    }

    _removeOffClickHandler() {
        if (typeof this._offClickHandler === 'function') {
            document.removeEventListener('click', this._offClickHandler, true);
            this._offClickHandler = null;
        }
    }

    _onChange(editorState) {
        this.setState({ editorState });
        const { onChange } = this.props;
        if (typeof onChange === 'function') {
            onChange(editorState);
        }
        return editorState;
    }

    _onBlur(e) {
        const { onBlur } = this.props;
        if (typeof onBlur === 'function') {
            onBlur(this.state.editorState);
        }
    }

    _handleKeyCommand(command) {
        const { editorState } = this.state;
        const newState = RichUtils.handleKeyCommand(editorState, command);
        if (newState) {
            this.onChange(newState);
            return true;
        }
        return false;
    }

    _onTab(e) {
        const maxDepth = 4;
        const newState = RichUtils.onTab(e, this.state.editorState, maxDepth);
        if (newState) {
            this.onChange(newState);
            return true;
        }
        return false;
    }

    _onDelete() {
        const newState = RichUtils.onDelete(this.state.editorState);
        if (newState) {
            this.onChange(newState);
            return true;
        }
        return false;
    }

    _onBackspace() {
        const newState = RichUtils.onBackspace(this.state.editorState);
        if (newState) {
            this.onChange(newState);
            return true;
        }
        return false;
    }

    render() {
        const { editorState } = this.state;
        const { readOnly } = this.props;
        const filteredProps = omit(this.props, [
            'ref',
            'editorState',
            'handleKeyCommand',
            'plugins',
            'onChange',
            'onTab',
            'onDelete',
            'onBackspace',
            'onBlur'
        ]);
        return (
            <div className={styles.standardRichTextEditor} onClick={() => this.focus()} data-cy="StandardRichTextEditor">
                <ToolbarHost editorState={editorState} readOnly={readOnly}/>
                <div className={styles.richTextEditorArea}>
                    <Editor {...filteredProps}
                        ref={e => this.editor = e}
                        editorState={editorState}
                        plugins={plugins}
                        handleKeyCommand={this.handleKeyCommand}
                        onChange={this.onChange}
                        onTab={this.onTab}
                        onDelete={this.onDelete}
                        onBackspace={this.onBackspace}
                        onBlur={this.onBlur}
                    />
                </div>
            </div>
        );
    }
}

export default StandardRichTextEditor;

